import { useEffect, useMemo, useState } from "react";
import dateformat from "dateformat";
import { Paper } from "@mui/material";
import Container from "@mui/material/Container";
import { createUseStyles } from "$utils/createUseStyles";
import { Text } from "$components";
import { publicIconPath } from "$utils/constants";
import { theme } from "$utils/theme/bataillog";
import { useJobs } from "$hooks/useJobs";
import * as JobService from "$services/Job.service";
import { JobRecord, JobResultKind, hasDownloadableFile } from "$types/Job";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import logger from "$utils/logger";

const fileNameOf = (job: JobRecord) =>
  job.result?.kind === JobResultKind.FILE ? job.result.fileName : job.label;

const finishedAtOf = (job: JobRecord) =>
  new Date(job.finishedAt ?? job.createdAt).getTime();

/**
 * Recent files produced by the current user's jobs.
 * The gateway already keeps them, scoped to the caller, until they expire.
 */
export const RecentDownloads = () => {
  const classes = useStyles();
  const { jobs, downloadJob } = useJobs();
  const [history, setHistory] = useState<JobRecord[]>([]);

  useEffect(() => {
    let cancelled = false;
    JobService.listJobs()
      .then((records) => {
        if (!cancelled) setHistory(records);
      })
      .catch((error) => logger.error(error));
    return () => {
      cancelled = true;
    };
  }, []);

  const downloads = useMemo(() => {
    const byId = new Map<string, JobRecord>();
    [...history, ...jobs].forEach((job) => {
      if (hasDownloadableFile(job)) byId.set(job.id, job);
    });
    return [...byId.values()].sort(
      (left, right) => finishedAtOf(right) - finishedAtOf(left)
    );
  }, [history, jobs]);

  return (
    <Paper className={classes.section}>
      <div className={classes.head}>
        <img alt="" src={`${publicIconPath}/download.svg`} />
        <div className={classes.titles}>
          <Text type="h3">Téléchargements</Text>
          <Text className={classes.caption}>Dernières 24 heures</Text>
        </div>
      </div>
      <Container className={classes.body}>
        {downloads.length === 0 ? (
          <Text className={classes.empty}>
            Aucun fichier sur les dernières 24 h.
          </Text>
        ) : (
          <ul className={classes.list}>
            {downloads.map((job) => (
              <li key={job.id} className={classes.row}>
                <button
                  type="button"
                  className={classes.link}
                  onClick={() => downloadJob(job)}
                >
                  {fileNameOf(job)}
                </button>
                <Text className={classes.date}>
                  {dateformat(
                    job.finishedAt ?? job.createdAt,
                    `${DEFAULT_DATE_FORMAT} HH'h'MM`
                  )}
                </Text>
              </li>
            ))}
          </ul>
        )}
      </Container>
    </Paper>
  );
};

const useStyles = createUseStyles(() => ({
  section: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    border: "0px",
    margin: "25px auto 0",
  },
  head: {
    padding: "12px 16px",
    borderBottom: "1px solid #d8dadc",
    display: "flex",
    alignItems: "center",
    gap: 10,
    "& h3": {
      margin: 0,
    },
  },
  titles: {
    display: "flex",
    flexDirection: "column",
  },
  caption: {
    color: theme.color.gray5,
    fontSize: 13,
  },
  body: {
    margin: "16px 0",
  },
  empty: {
    color: theme.color.gray5,
  },
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  row: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 16,
    padding: "8px 0",
    borderBottom: `1px solid ${theme.color.gray2}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  link: {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    textAlign: "left",
    color: theme.color.blue,
    fontFamily: theme.font.workSans,
    fontSize: 14,
    textDecoration: "underline",
  },
  date: {
    flexShrink: 0,
    color: theme.color.gray5,
    fontSize: 13,
  },
}));
